import * as React from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, IconButton, Stack, Skeleton, Paper,
    Box, Typography, Tooltip, Alert
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import { DataGrid, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { unwrapResult } from "@reduxjs/toolkit";

import { useAppDispatch } from "../../app/hooks";
import { StructureName } from "../device/Structure";
import { GampCategoryChip } from "../computerizedSystems/components/GampCategoryChip";
import { GampCategory } from "../computerizedSystems/gampDictionary";
import {
    loadComputerizedSystems,
    ComputerizedSystem,
} from "../computerizedSystems/computerizedSystemsApi";

type Props = {
    value: string | number | null;
    onChange: (id: string) => void;
    onSelectSystem?: (system: ComputerizedSystem | undefined) => void;
};

export const ComputerizedSystemSelectField: React.FC<Props> = ({
    value,
    onChange,
    onSelectSystem,
}) => {
    const dispatch = useAppDispatch();

    const [open, setOpen] = React.useState(false);
    const [selection, setSelection] = React.useState<GridRowSelectionModel>(value ? [String(value)] : []);
    const [rows, setRows] = React.useState<ComputerizedSystem[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isError, setIsError] = React.useState(false);
    const [error, setError] = React.useState<any>(null);

    React.useEffect(() => {
        setIsLoading(true);
        dispatch(loadComputerizedSystems())
            .then(unwrapResult)
            .then((list) => setRows(list ?? []))
            .catch((e) => {
                console.error(e);
                setIsError(true);
                setError(e);
            })
            .finally(() => setIsLoading(false));
    }, [dispatch]);

    React.useEffect(() => {
        setSelection(value ? [String(value)] : []);
    }, [value]);

    const selected = React.useMemo(
        () => rows.find(r => String(r.id) === String(value)),
        [rows, value]
    );

    const findById = (id: string | number) => rows.find(r => String(r.id) === String(id));

    const confirmSelection = () => {
        const id = selection.length ? String(selection[0]) : "";
        if (id) {
            onChange(id);
            onSelectSystem?.(findById(id));
        }
        setOpen(false);
    };

    const clearSelection = () => {
        setSelection([]);
        onChange("");
        onSelectSystem?.(undefined);
    };

    const handleDialogClose = (_e: object, reason: "backdropClick" | "escapeKeyDown") => {
        if (reason === "backdropClick") return;
        setOpen(false);
    };

    const columns = React.useMemo<GridColDef[]>(
        () => [
            { field: "name", headerName: "Nazwa", flex: 1, minWidth: 220 },
            {
                field: "structureId",
                headerName: "Struktura",
                flex: 1,
                minWidth: 200,
                renderCell: (params) => <StructureName structureId={params.row.structureId} />,
            },
            { field: "vendor", headerName: "Vendor", minWidth: 160, flex: 0.7 },
            { field: "softwareVersion", headerName: "Wersja", minWidth: 120, flex: 0.5 },
            {
                field: "gampCategory",
                headerName: "GAMP 5",
                minWidth: 160,
                flex: 0.6,
                align: "center",
                headerAlign: "center",
                renderCell: (params) => (
                    <GampCategoryChip category={params.value as GampCategory} lang="pl" />
                ),
                sortComparator: (v1, v2) => Number(v1) - Number(v2),
            },
        ],
        []
    );

    return (
        <>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                {value ? (
                    <CheckCircleIcon sx={{ color: "success.main", fontSize: 18 }} aria-hidden />
                ) : (
                    <PriorityHighIcon sx={{ color: "error.main", fontSize: 18 }} aria-hidden />
                )}
                <Typography
                    variant="body2"
                    fontWeight={700}
                    sx={{ color: value ? "text.secondary" : "error.main" }}
                >
                    {value ? "Wybrany system" : "Wybierz system"}
                </Typography>
            </Stack>

            <Paper
                variant="outlined"
                onClick={() => setOpen(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setOpen(true);
                    }
                }}
                sx={(theme) => ({
                    p: 1.25,
                    borderRadius: 1.5,
                    cursor: "pointer",
                    borderColor: theme.palette.divider,
                    "&:hover": { borderColor: theme.palette.text.primary },
                    "&:focus-visible": {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: 2,
                    },
                })}
            >
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography variant="subtitle2" fontWeight={700}>
                            Nazwa: {selected?.name ?? "—"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Vendor: {selected?.vendor ?? "—"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Wersja: {selected?.softwareVersion ?? "—"}
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={0.5} alignItems="center">
                        {value && (
                            <Tooltip title="Wyczyść wybór">
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        clearSelection();
                                    }}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title="Wybierz system">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpen(true);
                                }}
                            >
                                <SearchIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            </Paper>

            <Dialog open={open} onClose={handleDialogClose} maxWidth="lg" fullWidth>
                <DialogTitle>Wybierz system skomputeryzowany</DialogTitle>
                <DialogContent dividers>
                    {isLoading ? (
                        <Skeleton variant="rectangular" height="60vh" />
                    ) : isError ? (
                        <Alert severity="error">
                            Błąd ładowania listy systemów.
                            {error?.message ? ` (${String(error.message)})` : null}
                        </Alert>
                    ) : (
                        <Box sx={{ height: "60vh", minHeight: 300, width: "100%" }}>
                            <DataGrid
                                rows={rows}
                                columns={columns}
                                density="compact"
                                rowSelectionModel={selection}
                                onRowSelectionModelChange={(model) => {
                                    setSelection(model.length ? [String(model[0])] : []);
                                }}
                                initialState={{
                                    sorting: { sortModel: [{ field: "name", sort: "asc" }] },
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Anuluj</Button>
                    <Button variant="contained" onClick={confirmSelection} disabled={!selection.length}>
                        Wybierz
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
